import React, { useState, useEffect } from 'react';
import { Table } from './common/Table';
import { Loading } from './common/Loading';
import { ReportCard } from './common/ReportCard';
import axios from 'axios';

const apiUrl = window._env_?.REACT_APP_API_URL || "https://app.mylocaleventrentals.com/api";

// Custom debounce hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const ProfitReport = () => {
    const [reportData, setReportData] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const debouncedSearch = useDebounce(search, 500);

    const columns = [
        { key: 'name', label: 'Item Name', sortable: true },
        { key: 'purchasePrice', label: 'Purchase Price', sortable: true },
        { key: 'rentalCount', label: 'Total Rentals', sortable: true },
        { key: 'totalRevenue', label: 'Total Rental Income', sortable: true },
        { key: 'profit', label: 'Profit', sortable: true },
    ];

    useEffect(() => {
        const fetchProfitReport = async () => {
            setError("");
            setLoading(true);
            const token = localStorage.getItem('token');
    const rentalData = await axios.get(`${apiUrl}/report/inventory-profitability`, {
      headers: { 'x-auth-token': token }
    })
                .then(res => {
                    if (!res.data || !Array.isArray(res.data))   {
                        throw new Error(`Error fetching report: ${res.statusText}`);
                    }
                    return res.data;
                })
                .catch(err => {
                    console.error(err);
                    setError("Failed to fetch report data. Please try again later.");
                    setLoading(false);
                    return [];
                });
            // Using dummy data as backend is not available
 

            // Filter data based on search
            const filteredData = rentalData.filter(item =>
                item.name.toLowerCase().includes(debouncedSearch.toLowerCase())
            );

            setReportData(filteredData);
            setLoading(false);
        };

        fetchProfitReport();
    }, [debouncedSearch]);

    if (loading) {
        return <Loading text="Fetching your data..." fullScreen />;
    }

    return (
        <ReportCard title="Profitability Report">
            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center justify-between">
                    <span>{error}</span>
                    <button onClick={() => setError("")} className="text-red-700 hover:text-red-900 font-bold">
                        ✕
                    </button>
                </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mb-3">
                <div className="relative flex-grow">
                    <input
                        type="text"
                        placeholder="Search by item name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    />
                    {search && (
                        <button
                            onClick={() => setSearch("")}
                            className="absolute right-3 top-2 text-gray-500 hover:text-gray-700 font-bold"
                            title="Clear search"
                        >
                            ✕
                        </button>
                    )}
                </div>
            </div>

            {reportData.length === 0 && !loading ? (
                <div className="text-center py-8 text-gray-500">
                    {search ? `No items found matching "${search}"` : "No data available"}
                </div>
            ) : (
                <Table
                    columns={columns}
                    data={reportData}
                    paginated={true}
                    pageSize={10}
                    rowKey="id"
                />
            )}
        </ReportCard>
    );
};

export default ProfitReport;